import React, { FC, RefObject } from "react";
import { Tab } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";
import { PlanManagementProvider } from "../billing/PlanManagement/PlanManagementProvider";
import { useTypedFlags } from "../../hooks/useTypedFlags";
import { useBillingType } from "../../hooks/useBillingType";
import { SubscriptionType } from "../../common/types/billing";
import { useBillingManagement } from "../../hooks/useBillingManagement";
import { UserSettingsPages, WorkspaceSettingsPages } from "./SettingsProvider";
import { DirectorySettings } from "./Directory/DirectorySettings";
import { BillingPage } from "./Billing/BillingPage";
import { PlansPage } from "./Plans/PlansPage";
import { WorkspaceMembers } from "./WorkspaceMembers/WorkspaceMembers";
import { DirectoryContextProvider } from "./Directory/DirectoryContextProvider";
import { IntegrationsSettings } from "./Integrations/IntegrationsSettings";
import { WorkspaceLock } from "./Lock/WorkspaceLock";

interface Props {
    scrollbars: RefObject<Scrollbars>;
}

export const WorkspacePanes: FC<Props> = ({ scrollbars }) => {
    const {
        directory: canUseDirectory,
        workspaceMemberUi: canUseWorkspaceMembers,
        locks: canUseLocks,
    } = useTypedFlags();
    const { canManageBilling } = useBillingManagement();
    const billingType = useBillingType();
    const showPlans =
        canManageBilling && billingType === SubscriptionType.FLAT_RATE;

    return (
        <>
            {canUseWorkspaceMembers && (
                <Tab.Pane
                    eventKey={WorkspaceSettingsPages.MEMBERS}
                    title="Members"
                >
                    <WorkspaceMembers />
                </Tab.Pane>
            )}

            <Tab.Pane
                eventKey={WorkspaceSettingsPages.INTEGRATIONS}
                title="Integrations"
            >
                <IntegrationsSettings scrollbars={scrollbars} />
            </Tab.Pane>

            {canUseDirectory && (
                <Tab.Pane eventKey={WorkspaceSettingsPages.DIRECTORY}>
                    <PlanManagementProvider>
                        <DirectoryContextProvider>
                            <DirectorySettings />
                        </DirectoryContextProvider>
                    </PlanManagementProvider>
                </Tab.Pane>
            )}

            {canUseLocks && (
                <Tab.Pane eventKey={WorkspaceSettingsPages.LOCK} title="Locks">
                    <WorkspaceLock />
                </Tab.Pane>
            )}

            {canManageBilling && (
                <Tab.Pane eventKey={UserSettingsPages.BILLING} title="Billing">
                    <BillingPage />
                </Tab.Pane>
            )}

            {showPlans && (
                <Tab.Pane eventKey={UserSettingsPages.PLANS} title="Plans">
                    <PlanManagementProvider>
                        <PlansPage />
                    </PlanManagementProvider>
                </Tab.Pane>
            )}
        </>
    );
};
